import { LoginForm, BackendAuthResponse, AuthResponse } from '@/types/auth';

import { BASE_URL, ApiError } from './base.service';

/**
 * Main API Client
 * Exports auth functions and re-exports all service modules
 */

// Transform backend auth response to our internal format
const transformAuthResponse = (backendResponse: BackendAuthResponse): AuthResponse => {
  const primaryRole = backendResponse.userContext.isAdminOnboarding
    ? 'admin_onboarding'
    : (backendResponse.userContext.roles[0]?.name ?? 'employee');

  // Calculate expiration timestamp
  const expiresAt = Date.now() + backendResponse.expires_in * 1000;

  // Create user object with proper format
  const user = {
    id: backendResponse.user.id,
    email: backendResponse.user.email,
    name: backendResponse.user.email.split('@')[0], // Extract name from email for now
    role: primaryRole,
    created_at: backendResponse.user.created_at,
    updated_at: backendResponse.user.updated_at,
  };

  return {
    user,
    token: backendResponse.access_token,
    expiresAt,
    userContext: backendResponse.userContext,
  };
};

/**
 * Check if token is expired
 */
export const isTokenExpired = (expiresAt: number | null): boolean => {
  if (!expiresAt) return true;
  return Date.now() >= expiresAt;
};

/**
 * Get stored token with expiration check
 */
export const getStoredToken = (): string | null => {
  const token = localStorage.getItem('finara_token');
  const expiresAt = localStorage.getItem('finara_token_expires');

  if (!token || !expiresAt) return null;

  if (isTokenExpired(parseInt(expiresAt))) {
    // Token expired, clear storage
    localStorage.removeItem('finara_token');
    localStorage.removeItem('finara_token_expires');
    localStorage.removeItem('finara_user_id');
    return null;
  }

  return token;
};

/**
 * Authentication Service
 */
export const authService = {
  async login(credentials: LoginForm): Promise<AuthResponse> {
    try {
      const response = await fetch(`${BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: credentials.email,
          password: credentials.password,
        }),
      });

      if (!response.ok) {
        await response.json().catch(() => ({}));

        throw new ApiError('Error al iniciar sesión');
      }

      const backendData = (await response.json()) as BackendAuthResponse;
      return transformAuthResponse(backendData);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }

      // Network or other errors
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new ApiError('No se pudo conectar con el servidor. Verifica tu conexión a internet.');
      }

      throw new ApiError('Error inesperado. Intenta nuevamente.');
    }
  },

  async resetPassword(
    accessToken: string,
    refreshToken: string,
    newPassword: string
  ): Promise<{ success: boolean; message: string }> {
    try {
      const response = await fetch(`${BASE_URL}/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          access_token: accessToken,
          refresh_token: refreshToken,
          new_password: newPassword,
        }),
      });

      if (!response.ok) {
        const errorData = (await response.json().catch(() => ({}))) as { message?: string };
        throw new ApiError(
          errorData.message ?? 'Error al restablecer la contraseña',
          response.status
        );
      }

      return (await response.json()) as { success: boolean; message: string };
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }

      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new ApiError('No se pudo conectar con el servidor. Verifica tu conexión a internet.');
      }

      throw new ApiError('Error inesperado. Intenta nuevamente.');
    }
  },
};

/**
 * Export all services organized by backend module
 */
export { accountsService } from './accounts.service';
export { analyticsService } from './analytics.service';
export { cardsService } from './cards.service';
export { creditsService } from './credits.service';
export { departmentsService } from './departments.service';
export { employeesService } from './employees.service';
export { jobTitlesService } from './jobTitles.service';
export { notificationsService } from './notifications.service';
export { onboardingService } from './onboarding.service';
export { organizationsService } from './organizations.service';
export { rolesService } from './roles.service';
export { salariesService } from './salaries.service';
export { transactionsService } from './transactions.service';
export { usersService } from './users.service';
export { adminOnboardingService } from './adminOnboarding.service';

/**
 * Unified API Client Object (for backward compatibility)
 * This object provides a single import point for all API methods
 */
export const apiClient = {
  // Auth
  login: (credentials: LoginForm) => authService.login(credentials),
};
